import { createClient, SupabaseClient } from '@supabase/supabase-js';

declare global {
  interface Window {
    __SUPABASE_CLIENT__?: SupabaseClient;
  }
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://eneiufxbxiltaoczjybq.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVuZWl1ZnhieGlsdGFvY3pqeWJxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE1MTMwMzIsImV4cCI6MjA1NzA4OTAzMn0.XjwFg8iMhJsMsuBzbCpym7iZwbfbp_9_AkHWcyS_Hwc';

// Check and validate environment variables
if (!supabaseUrl || supabaseUrl.length < 10) {
  console.error('Invalid or missing VITE_SUPABASE_URL:', supabaseUrl);
}

if (!supabaseAnonKey || supabaseAnonKey.length < 20) {
  console.error('Invalid or missing VITE_SUPABASE_ANON_KEY');
}

// Check if we already have a client in the window object
let supabase: SupabaseClient;

try {
  if (window.__SUPABASE_CLIENT__) {
    supabase = window.__SUPABASE_CLIENT__;
  } else {
    // Create a new client
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
        debug: false
      },
      global: {
        headers: {
          'X-Client-Info': 'flows-cash-web'
        }
      }
    });
    
    // Verify the client was created properly
    if (!supabase || !supabase.auth) {
      throw new Error('Failed to initialize Supabase client properly');
    }
    
    // Store the client in the window object for reuse
    window.__SUPABASE_CLIENT__ = supabase;
  }
} catch (error) {
  console.error('Error initializing Supabase client:', error);
  // Create a fallback client with minimal options
  supabase = createClient(supabaseUrl, supabaseAnonKey, {
    auth: { debug: false }
  });
  window.__SUPABASE_CLIENT__ = supabase;
}

/**
 * Sign in with Google - Use this function instead of direct API calls
 * This ensures the correct flow for web
 */
export const signInWithGoogle = async () => {
  // For Google OAuth, always use the Supabase callback URL to avoid redirect_uri_mismatch errors
  
  // Hard-code the exact redirect URL from the Google Cloud Console authorized redirects
  const redirectTo = 'https://eneiufxbxiltaoczjybq.supabase.co/auth/v1/callback';
  
  return supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo,
      skipBrowserRedirect: false, // Ensure browser redirects happen
      queryParams: {
        access_type: 'offline',
        prompt: 'consent',
        // Explicitly set scopes to match what's configured in Google Cloud Console
        scope: 'openid email profile'
      }
    }
  });
};

/**
 * Handle a callback from OAuth - this should be called early in your app initialization
 * or from the auth callback page
 */
export const handleAuthCallback = async () => {
  const hashParams = window.location.hash;
  const queryParams = window.location.search;
  
  // First try to get the current session without exchange
  try {
    const { data: initialSession } = await supabase.auth.getSession();
    if (initialSession.session) {
      return { success: true, session: initialSession.session };
    }
  } catch (error) {
    console.error('Error checking initial session:', error);
  }
  
  // If we have auth parameters, try to exchange them for a session
  if (hashParams || queryParams) {
    try {
      // getSession will handle token exchange if needed
      const { data, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Auth callback error during getSession:', error);
        return { success: false, error };
      }
      
      if (!data.session) {
        // Try to check user status as fallback
        const { data: userData } = await supabase.auth.getUser();
        
        if (userData?.user) {
          // Try refreshing session
          const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError) {
            console.error('Session refresh failed:', refreshError);
          } else if (refreshData.session) {
            return { success: true, session: refreshData.session };
          }
        }
        
        return { 
          success: false, 
          error: new Error('No session established after authentication')
        };
      }
      
      return { success: true, session: data.session };
    } catch (err) {
      console.error('Error handling auth callback:', err);
      return { success: false, error: err };
    }
  }
  
  return { success: false, error: 'No auth parameters found in URL' };
};

/**
 * Direct Google Sign-In function that explicitly sets all parameters
 * Use this as a last resort if other methods fail
 */
export const directGoogleSignIn = async () => {
  // Use the exact redirect URL from Google Cloud Console
  const redirectUrl = 'https://eneiufxbxiltaoczjybq.supabase.co/auth/v1/callback';
  
  // Use the client ID from the downloaded JSON file
  const clientId = '1073300935034-02le35iune4a4q8hv4heqqke1cer0die.apps.googleusercontent.com';
  
  // Calculate the Google OAuth URL
  const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
    `client_id=${encodeURIComponent(clientId)}` +
    `&redirect_uri=${encodeURIComponent(redirectUrl)}` +
    `&response_type=code` +
    `&scope=${encodeURIComponent('openid email profile')}` +
    `&access_type=offline` +
    `&prompt=consent`;
  
  // Redirect the browser
  window.location.href = googleAuthUrl;
  
  // Return a dummy result since we're redirecting
  return { data: null, error: null };
};

export { supabase };
export default supabase; 